import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";

const SEED_CATEGORIES = [
  { id: "triage", label: "Triage" },
  { id: "triage_scenario", label: "Referral Scenarios" },
  { id: "sdoh", label: "SDOH" },
  { id: "instructions", label: "Instructions" },
];

const SEED_SPECIALTIES = [
  { id: "oncology", label: "Oncology" },
  { id: "orthopedics", label: "Orthopedics" },
];

function humanizeCategory(slug) {
  return slug
    .split(/[_-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function LibraryProtocols() {
  const nav = useNavigate();
  const { env, isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();
  const [category, setCategory] = useState("all");
  const [specialty, setSpecialty] = useState("all");
  const [allProtocols, setAllProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Always fetch ALL protocols so we can derive dynamic category + specialty
  // lists from what actually exists. Filtering is applied in-memory.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.listProtocols({ mode: env });
        setAllProtocols(
          (res.data || []).sort((a, b) => {
            if (a.category !== b.category) return a.category.localeCompare(b.category);
            return a.protocol_id.localeCompare(b.protocol_id);
          })
        );
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [env]);

  // Derive dynamic category list: seed categories + any custom ones found in data
  const categoryTabs = useMemo(() => {
    const seedIds = new Set(SEED_CATEGORIES.map((c) => c.id));
    const customIds = new Set();
    for (const p of allProtocols) {
      if (p.category && !seedIds.has(p.category)) customIds.add(p.category);
    }
    const tabs = [{ id: "all", label: "All" }, ...SEED_CATEGORIES];
    for (const c of [...customIds].sort()) {
      tabs.push({ id: c, label: humanizeCategory(c) });
    }
    return tabs;
  }, [allProtocols]);

  // Derive dynamic specialty filter
  const specialtyFilters = useMemo(() => {
    const seedIds = new Set(SEED_SPECIALTIES.map((s) => s.id));
    const customIds = new Set();
    for (const p of allProtocols) {
      for (const s of p.specialties || []) {
        if (s && !seedIds.has(s)) customIds.add(s);
      }
    }
    const filters = [
      { id: "all", label: "All specialties" },
      ...SEED_SPECIALTIES,
    ];
    for (const s of [...customIds].sort()) {
      filters.push({ id: s, label: humanizeCategory(s) });
    }
    return filters;
  }, [allProtocols]);

  // Apply filters in-memory
  const protocols = useMemo(() => {
    return allProtocols.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (specialty !== "all" && !(p.specialties || []).includes(specialty))
        return false;
      return true;
    });
  }, [allProtocols, category, specialty]);

  // Group by category for display when "all" is selected
  const grouped = {};
  for (const p of protocols) {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  }

  return (
    <>
      <PageHeader
        title="Agent Rules"
        subtitle="Reusable rules that agents load at runtime as functions. Tag each rule with the categories and specialties where it applies — triage flows, referral scenarios, SDOH domains, visit prep, and anything else your agents need to pull in on demand."
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Agent Rules" },
        ]}
        actions={
          isLive ? null : (
            <button
              className="btn primary"
              onClick={() => nav("/library/draft/protocols/new")}
            >
              + New rule
            </button>
          )
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div className="mb-16" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="muted small">Category:</span>
          <div className="btn-row" style={{ flexWrap: "wrap" }}>
            {categoryTabs.map((c) => {
              const n =
                c.id === "all"
                  ? allProtocols.length
                  : allProtocols.filter((p) => p.category === c.id).length;
              return (
                <button
                  key={c.id}
                  className={`btn sm ${category === c.id ? "primary" : ""}`}
                  onClick={() => setCategory(c.id)}
                >
                  {c.label}
                  <span className="muted" style={{ marginLeft: 4 }}>
                    ({n})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-16" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="muted small">Specialty:</span>
          <div className="btn-row" style={{ flexWrap: "wrap" }}>
            {specialtyFilters.map((s) => {
              const n =
                s.id === "all"
                  ? allProtocols.length
                  : allProtocols.filter((p) =>
                      (p.specialties || []).includes(s.id)
                    ).length;
              return (
                <button
                  key={s.id}
                  className={`btn sm ${specialty === s.id ? "primary" : ""}`}
                  onClick={() => setSpecialty(s.id)}
                >
                  {s.label}
                  <span className="muted" style={{ marginLeft: 4 }}>
                    ({n})
                  </span>
                </button>
              );
            })}
          </div>
          <div className="spacer" />
          <span className="muted small">
            {protocols.length} rule{protocols.length === 1 ? "" : "s"}
          </span>
        </div>


        {loading ? (
          <Loading />
        ) : protocols.length === 0 ? (
          <div className="empty-state">
            <h3>No protocols</h3>
            <p>Try widening the filters.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="card" style={{ padding: 0 }}>
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--color-border)",
                  background: "var(--color-surface-muted)",
                }}
              >
                <strong style={{ fontSize: 13 }}>{categoryLabel(cat)}</strong>
                <span className="muted small" style={{ marginLeft: 8 }}>
                  {list.length} file{list.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="version-list">
                {list.map((p) => (
                  <li key={`${p.category}-${p.protocol_id}`}>
                    <TxtIcon />
                    <div className="version-meta">
                      <div className="commit">
                        <Link
                          to={`${prefix}/protocols/${encodeURIComponent(p.category)}/${encodeURIComponent(p.protocol_id)}`}
                        >
                          <strong>{p.protocol_id}</strong>
                        </Link>
                      </div>
                      <div className="author">{p.description || p.name || ""}</div>
                    </div>
                    <div className="btn-row">
                      {(p.specialties || []).map((s) => (
                        <span key={s} className="badge badge-neutral">{s}</span>
                      ))}
                      {(p.required_tools || []).length > 0 && (
                        <span
                          className="badge badge-accent"
                          title={(p.required_tools || []).join(", ")}
                        >
                          {(p.required_tools || []).length} tool
                          {(p.required_tools || []).length === 1 ? "" : "s"}
                        </span>
                      )}
                      <span className="muted small mono">
                        {(p.content?.length || 0).toLocaleString()} chars
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function categoryLabel(cat) {
  const entry = SEED_CATEGORIES.find((c) => c.id === cat);
  return entry?.label || humanizeCategory(cat);
}

function TxtIcon() {
  return (
    <div
      style={{
        width: 36,
        height: 36,
        background: "var(--color-surface-muted)",
        border: "1px solid var(--color-border)",
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 700,
        color: "var(--color-text-muted)",
        flexShrink: 0,
      }}
    >
      TXT
    </div>
  );
}
